/**
 * What a notification says, as plain strings — one place for the toast, the
 * inbox row and the screen-reader label, so the three never disagree. Mirrors
 * Blockari's `shell/notify-copy.ts`.
 */

import { formatScore } from '@/ui/hud';
import { chestNoticeLine } from '@/game/chest-label';
import type { AppNotification } from '@/net/api';

/**
 * The line, and the tail a wide toast may add after it.
 *
 * A result is the whole line, for either seat: "You beat nova" / "You lost to
 * nova". (A challenge always has a winner — a tie in score falls to time, then
 * to who submitted first — so there is no "tied" notice.) It needs no suffix:
 * the eyebrow already says WON or LOST. Only a take keeps one ("· playing now"),
 * which a phone drops.
 */
export function sentence(n: AppNotification): { lead: string; suffix: string } {
  const who = n.opponent || 'Someone';
  switch (n.kind) {
    case 'challenge_taken':
      return { lead: `${who} took your challenge`, suffix: 'playing now' };
    case 'challenge_won':
      return { lead: `You beat ${who}`, suffix: '' };
    case 'challenge_lost':
      return { lead: `You lost to ${who}`, suffix: '' };
    case 'challenge_expired':
      return { lead: `No one took it · ${formatScore(Math.abs(n.amount))} $CHAIN refunded`, suffix: '' };
    case 'challenge_voided':
      // Amount 0: the stake had already been paid back (by hand) before the void, so nothing more comes back.
      return {
        lead: n.amount === 0 ? 'Voided · your stake was already returned' : `Voided · ${formatScore(Math.abs(n.amount))} $CHAIN refunded`,
        suffix: '',
      };
    case 'chest_earned':
      // No opponent and no score: a chest is something the player did, not
      // something that happened between two of them. It says what it was for ("Epic chest for 2nd place on the
      // weekly $CHAIN board", "Rare chest · 5 wins in a row"; the toast opens it, so no "open it" tail); an older
      // notice, with no label kept, says it plainly.
      return n.chestLabel && n.chestRarity
        ? { lead: chestNoticeLine(n.chestRarity, n.chestLabel), suffix: '' }
        : { lead: 'You earned a chest', suffix: 'open it' };
  }
}

/**
 * The line as it is drawn: a chest's "...for 3rd place on the weekly card streaks board" must not break after "on" (a
 * line that ends on a preposition reads as cut off; `text-wrap: balance` happily leaves it there), so "on the weekly" is held
 * together by no-break spaces (the break falls before "on": "...for 3rd place" / "on the weekly card streaks board"). Text only: the screen-reader sentence (`summaryText`) keeps the plain spaces.
 */
export const drawnLine = (lead: string): string => lead.replace(/ on the weekly /g, ' on\u00a0the\u00a0weekly ');

/** What joins the line to its suffix: "· playing now" for a take, "— open it" for a chest. */
export function suffixSep(n: AppNotification): string {
  return n.kind === 'challenge_taken' ? '\u00b7' : '\u2014';
}

/** `+50 $CHAIN` / `−5 $CHAIN`. A real minus sign, not a hyphen. */
export function amountText(amount: number): string {
  return `${amount >= 0 ? '+' : '−'}${formatScore(Math.abs(amount))} $CHAIN`;
}

export function scoresText(n: AppNotification): string | null {
  if (n.myScore === null || n.theirScore === null) return null;
  return `${formatScore(n.myScore)} vs ${formatScore(n.theirScore)}`;
}

/**
 * The whole notification as one sentence, for a screen reader:
 * "You beat nova · +20 $CHAIN · 8,470 vs 5,120".
 */
export function summaryText(n: AppNotification): string {
  const { lead, suffix } = sentence(n);
  const result = n.kind === 'challenge_won' || n.kind === 'challenge_lost';
  const amount = n.amount !== 0 ? `${result ? ' · ' : ' '}${amountText(n.amount)}` : '';
  const scores = scoresText(n);
  return `${lead}${suffix ? ` ${suffixSep(n)} ${suffix}` : ''}${amount}${scores ? ` · ${scores}` : ''}`;
}
